// src/features/chat/ConversationSummaryPanel.tsx
//
// AI-generated conversation summary (chat.GetConversationSummary) — key
// decisions, open questions, and a plain-language digest. Rendered in the
// same "muted box + details list" style as ExecutionDetailPage's
// AiOutputView (src/pages/workflow/ExecutionDetailPage.tsx), the
// established precedent for showing model-generated output in this app.

import { useEffect, useState } from 'react';
import { chatApi } from '@core/api';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { formatRelativeTime } from './logic';

export interface ConversationSummaryPanelProps {
  conversationId: string;
  locale: string;
  onClose: () => void;
}

export function ConversationSummaryPanel({ conversationId, locale, onClose }: ConversationSummaryPanelProps) {
  const { t } = useI18n();
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof chatApi.GetConversationSummary>> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await chatApi.GetConversationSummary(conversationId);
      setSummary(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  return (
    <div className="flex h-full w-96 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-1.5">
          <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
          </svg>
          <h3 className="text-sm font-semibold text-foreground">{t('chat.summary.title')}</h3>
        </div>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('chat.summary.generating')}</div>
        ) : error ? (
          <div className="text-sm text-destructive">{error}</div>
        ) : !summary ? null : (
          <>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{t('chat.summary.messageCount', { count: summary.message_count })}</span>
              <span>·</span>
              <span>{t('chat.summary.participantCount', { count: summary.participant_count })}</span>
            </div>

            {summary.summary && (
              <div className="rounded-md bg-muted p-3 text-sm text-foreground whitespace-pre-wrap">
                {summary.summary}
              </div>
            )}

            {summary.decisions.length > 0 && (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('chat.summary.decisions')}
                </h4>
                <ul className="space-y-2">
                  {summary.decisions.map((d, idx) => (
                    <li key={idx} className="rounded-md border border-border p-2 text-sm">
                      <p className="text-foreground">{d.decision}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {d.decided_by?.display_name || t('chat.unknownUser')}
                        {d.decided_at ? ` · ${formatRelativeTime(d.decided_at, locale)}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.open_questions.length > 0 && (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('chat.summary.openQuestions')}
                </h4>
                <ul className="space-y-2">
                  {summary.open_questions.map((q, idx) => (
                    <li key={idx} className="rounded-md border border-border p-2 text-sm">
                      <p className="text-foreground">{q.question}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {q.asked_by?.display_name || t('chat.unknownUser')}
                        {q.asked_at ? ` · ${formatRelativeTime(q.asked_at, locale)}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.generated_at && (
              <p className="text-[10px] text-muted-foreground">
                {t('chat.summary.generatedAt', { time: formatRelativeTime(summary.generated_at, locale) })}
              </p>
            )}
          </>
        )}
      </div>

      <div className="border-t border-border p-3">
        <Button size="sm" variant="secondary" onClick={load} isLoading={isLoading} className="w-full">
          {t('chat.summary.refresh')}
        </Button>
      </div>
    </div>
  );
}

export default ConversationSummaryPanel;
